// eyeq — link-annotation contracts (ADR-0052). A frame that landed in a
// url_required cell with an empty URL becomes a LINK TASK addressed to one
// person: by default the uploader (their own frames, and frames uploaded on
// their behalf), or a dedicated link-QA annotator a manager assigned it to.
// The tool is one screen with an adaptive layout — thumb-sized on mobile,
// keyboard-driven on desktop — so the contracts below are shared by both.
//
// Everything here is deterministic and pure: the URL format rules already live
// in ./upload (eyeqUrlFormatNote / eyeqNormalizeUrl) and are NOT forked; this
// file adds the task shapes, the brand heuristic used to batch tasks, and the
// SSRF host rules the urlPreview fetch is judged against.
import { z } from 'zod';
import { EyeqUrlCandidateSchema } from './manager';

// ── task state ──────────────────────────────────────────────────────────────
// pending        — waiting for a human
// done           — a URL was attached (frozen into eyeq_asset_meta.url)
// no_page        — house brand / the product genuinely has no page anywhere
// not_a_product  — the frame is in the wrong cell; a manager decides, the
//                  annotator only flags it (meta is NOT modified)
// skipped        — "not now": stays out of the queue, a manager can reassign
export const EYEQ_LINK_STATES = ['pending', 'done', 'no_page', 'not_a_product', 'skipped'] as const;
export const EyeqLinkStateSchema = z.enum(EYEQ_LINK_STATES);
export type EyeqLinkState = z.infer<typeof EyeqLinkStateSchema>;

// reject_candidate is not a verdict on the TASK — it kills one proposed URL and
// leaves the task pending. It exists because "Не то" on a candidate card has to
// survive a reload: a rejection that only lived in the browser would re-offer
// the same wrong page tomorrow.
export const EyeqLinkActionSchema = z.enum([
  'attach', 'no_page', 'not_a_product', 'skip', 'reject_candidate',
]);
export type EyeqLinkAction = z.infer<typeof EyeqLinkActionSchema>;

// ── brand heuristic ─────────────────────────────────────────────────────────
// Tasks are worked in brand batches: one warm tab, twelve frames, no re-orienting
// between them — that grouping is the single biggest multiplier on annotation
// speed. The brand therefore has to come from data we already hold, and the
// only text that could carry it is the participant's own subject line.
//
// It is deliberately CONSERVATIVE: a wrong brand splits a batch and costs more
// than no brand at all (unbranded tasks simply share the leftover bucket). So a
// leading token is accepted only on a strong signal — ALL-CAPS (IKEA, LIDL),
// an internal capital (iPhone, PlayStation, McDonald's), a digit (7Days), or an
// ampersand (H&M). "Ceramic coffee mug" yields '' because "Ceramic" looks
// exactly like every other sentence-cased first word.
//
// No model is called here and none should be: this runs inside the ingest path.
const BRAND_TOKEN = /^[A-Za-z0-9][A-Za-z0-9&'’.‑-]*$/;
// Digits alone are not a brand — "500ml" and "1L" are how people write sizes.
// A digit prefix counts only when a capitalised WORD follows it (7Days).
const DIGIT_BRAND = /^\d+[A-Z][A-Za-z]+/;

export function eyeqBrandFromSubject(subject: string): string {
  const first = (subject || '').trim().split(/\s+/)[0] ?? '';
  const tok = first.replace(/[,;:.]+$/, '');
  if (tok.length < 2 || tok.length > 32) return '';
  if (!BRAND_TOKEN.test(tok)) return '';
  const letters = tok.replace(/[^A-Za-z]/g, '');
  if (letters.length < 2) return '';
  const allCaps = letters === letters.toUpperCase();
  const innerCap = /[a-z][A-Z]/.test(tok);
  const hasAmp = tok.includes('&');
  if (!allCaps && !innerCap && !hasAmp && !DIGIT_BRAND.test(tok)) return '';
  return tok;
}

/** Pre-built web search for the annotator's "Искать" button. Plain search only:
 *  no coordinates, no city — a product page is found by name, and a coordinate
 *  in a URL we might store would put precise geolocation back into D1
 *  (ADR-0039 §2). */
export function eyeqLinkSearchUrl(subject: string, details: string, country: string): string {
  const q = [subject, details, country].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  return 'https://www.google.com/search?q=' + encodeURIComponent(q);
}

// ── urlPreview SSRF rules ───────────────────────────────────────────────────
// The worker fetches a participant-supplied URL, which makes it a request
// forger unless the target is constrained. These are HOST rules, judged before
// any socket is opened, and they are pure so both sides (and the tests) agree.
//
// Blocked, in order of how they get exploited:
//   · anything but https on the default port — no file://, no intent://, no
//     http://…:22 port-scanning;
//   · IP literals — v4 and v6 alike. A product page is never a bare IP, and
//     allowing them is how 169.254.169.254 (cloud metadata) gets read;
//   · single-label hosts (`intranet`) and internal suffixes — .internal,
//     .local, .home.arpa, localhost — the names private infrastructure answers to.
const INTERNAL_SUFFIXES = [
  'localhost', 'local', 'internal', 'intranet', 'lan', 'home.arpa',
  'localdomain', 'test', 'example', 'invalid', 'onion',
];

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/** True for an IPv4 literal that must never be fetched (loopback, RFC1918,
 *  link-local incl. cloud metadata, CGNAT, this-network, multicast, broadcast).
 *  Exported because it is the rule people actually want to read in a test. */
export function eyeqIsPrivateIPv4(host: string): boolean {
  const m = IPV4.exec(host);
  if (!m) return false;
  const o = m.slice(1).map(Number);
  if (o.some((n) => n > 255)) return true; // malformed → treat as unsafe
  const [a, b] = o;
  if (a === 0 || a === 10 || a === 127) return true;
  if (a === 169 && b === 254) return true;   // link-local + 169.254.169.254
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 192 && b === 0) return true;     // 192.0.0.0/24 + 192.0.2.0/24
  if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT
  if (a >= 224) return true;                 // multicast + reserved + broadcast
  return false;
}

/** Host-level allowlist decision for urlPreview. Every IP literal is blocked,
 *  private or not — a legitimate product page has a name. */
export function eyeqIsBlockedPreviewHost(host: string): boolean {
  const h = (host || '').trim().toLowerCase().replace(/\.$/, '');
  if (!h) return true;
  if (h.startsWith('[') || h.includes(':')) return true; // IPv6 literal
  if (IPV4.test(h)) return true;                          // any IPv4 literal
  if (/^\d+$/.test(h)) return true;                       // decimal-encoded IP
  if (!h.includes('.')) return true;                      // single-label host
  const labels = h.split('.');
  const last = labels[labels.length - 1];
  const last2 = labels.slice(-2).join('.');
  return INTERNAL_SUFFIXES.includes(last) || INTERNAL_SUFFIXES.includes(last2);
}

/** null = safe to fetch. 'url_format' — not an https URL on the default port;
 *  'blocked_host' — the host is one the worker must never reach. The Capture
 *  Protocol's own format rules (shorteners / social) are applied separately by
 *  eyeqUrlFormatNote — a preview of a shortener is pointless anyway. */
export function eyeqPreviewUrlNote(url: string): 'url_format' | 'blocked_host' | null {
  const s = (url || '').trim();
  if (!s) return 'url_format';
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return 'url_format';
  }
  if (u.protocol !== 'https:') return 'url_format';
  if (u.port !== '' && u.port !== '443') return 'url_format';
  if (u.username || u.password) return 'url_format';
  return eyeqIsBlockedPreviewHost(u.hostname) ? 'blocked_host' : null;
}

// urlPreview read budget: enough for a <title> in the document head, far too
// little to matter as an exfiltration channel or a memory problem.
export const EYEQ_PREVIEW_MAX_BYTES = 64 * 1024;
export const EYEQ_PREVIEW_TIMEOUT_MS = 5_000;
export const EYEQ_PREVIEW_MAX_REDIRECTS = 3;

// ── GET /api/eyeq/cabinet/linkTasks ─────────────────────────────────────────
export const EyeqLinkTasksQuerySchema = z.object({
  brand: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type EyeqLinkTasksQuery = z.infer<typeof EyeqLinkTasksQuerySchema>;

export const EyeqLinkTaskSchema = z.object({
  asset_id: z.string(),
  brand: z.string(),          // '' = the unbranded bucket
  state: EyeqLinkStateSchema,
  locale: z.string(),
  category: z.string(),
  sub_category: z.string(),
  subject_name: z.string(),
  disambiguation_details: z.string(),
  city: z.string(),
  country: z.string(),
  /** Worker path for the frame's bytes — participant-scoped, assignee only. */
  image_path: z.string(),
  /** Ready-made search the client opens in a new tab (never an iframe: the
   *  sites worth linking to all send X-Frame-Options). */
  search_url: z.string(),
  candidates: z.array(EyeqUrlCandidateSchema),
});
export type EyeqLinkTask = z.infer<typeof EyeqLinkTaskSchema>;

export const EyeqLinkBrandSchema = z.object({
  brand: z.string(),
  count: z.number().int(),
});

export const EyeqLinkTasksResSchema = z.object({
  ok: z.boolean(),
  /** Brand batches, biggest first — the warm-tab ordering IS the feature. */
  brands: z.array(EyeqLinkBrandSchema),
  items: z.array(EyeqLinkTaskSchema),
  pending: z.number().int(),
  /** Resolved by this person since UTC midnight — the shift counter. */
  done_today: z.number().int(),
});
export type EyeqLinkTasksRes = z.infer<typeof EyeqLinkTasksResSchema>;

// ── POST /api/eyeq/cabinet/linkTask ─────────────────────────────────────────
export const EyeqLinkTaskReqSchema = z.object({
  asset_id: z.string().min(1),
  action: EyeqLinkActionSchema,
  /** attach: required. Runs the SAME format gate as intake + the ≤10 per
   *  product per country cap; a refusal comes back as a categorical note. */
  url: z.string().trim().max(1000).optional(),
  /** attach: the candidate being confirmed (marked 'chosen').
   *  reject_candidate: the candidate being killed. */
  candidate_id: z.number().int().optional(),
});
export type EyeqLinkTaskReq = z.infer<typeof EyeqLinkTaskReqSchema>;

export const EyeqLinkTaskResSchema = z.object({
  ok: z.boolean(),
  asset_id: z.string(),
  state: EyeqLinkStateSchema,
  /** '' on success; otherwise categorical: url_cap | url_repeat | url_format |
   *  url_shortener | url_social. Never free text, never PII. */
  note: z.string().default(''),
  pending: z.number().int().default(0),
  done_today: z.number().int().default(0),
});
export type EyeqLinkTaskRes = z.infer<typeof EyeqLinkTaskResSchema>;

// ── GET /api/eyeq/cabinet/urlPreview?url= ───────────────────────────────────
export const EyeqUrlPreviewQuerySchema = z.object({
  url: z.string().trim().min(1).max(1000),
});
export type EyeqUrlPreviewQuery = z.infer<typeof EyeqUrlPreviewQuerySchema>;

export const EyeqUrlPreviewResSchema = z.object({
  ok: z.boolean(),
  host: z.string(),
  title: z.string(),
  /** '' | url_format | blocked_host | fetch_failed | too_many_redirects |
   *  http_error — categorical, so the cabinet can localize it. */
  note: z.string().default(''),
});
export type EyeqUrlPreviewRes = z.infer<typeof EyeqUrlPreviewResSchema>;

// ── POST /api/eyeq/manager/linkAssign ───────────────────────────────────────
// Bulk assignment over a filter. The target is named by PUBLIC id: the manager
// surface never handles Firebase uids (they are PII), and public_id is what the
// QA queue already shows.
export const EyeqManagerLinkAssignReqSchema = z.object({
  assignee_public_id: z.string().min(1).max(64),
  locale: z.string().max(16).optional(),
  category: z.string().max(120).optional(),
  sub_category: z.string().max(120).optional(),
  brand: z.string().max(64).optional(),
  /** Default filter = the whole point: frames in url_required cells that still
   *  have no URL. Set false to re-assign tasks that already exist. */
  only_missing_url: z.boolean().default(true),
  limit: z.number().int().min(1).max(500).default(100),
  dry_run: z.boolean().default(false),
});
export type EyeqManagerLinkAssignReq = z.infer<typeof EyeqManagerLinkAssignReqSchema>;

export const EyeqManagerLinkAssignResSchema = z.object({
  ok: z.boolean(),
  matched: z.number().int(),   // frames the filter selected
  assigned: z.number().int(),  // tasks now pointing at the assignee
  created: z.number().int(),   // tasks that did not exist before
});
export type EyeqManagerLinkAssignRes = z.infer<typeof EyeqManagerLinkAssignResSchema>;

// ── GET /api/eyeq/manager/linkProgress ──────────────────────────────────────
export const EyeqLinkProgressRowSchema = z.object({
  key: z.string(), // assignee public_id, or brand ('' = unbranded bucket)
  pending: z.number().int(),
  done: z.number().int(),
  no_page: z.number().int(),
  not_a_product: z.number().int(),
  skipped: z.number().int(),
});
export type EyeqLinkProgressRow = z.infer<typeof EyeqLinkProgressRowSchema>;

export const EyeqManagerLinkProgressResSchema = z.object({
  by_assignee: z.array(EyeqLinkProgressRowSchema),
  by_brand: z.array(EyeqLinkProgressRowSchema),
});
export type EyeqManagerLinkProgressRes = z.infer<typeof EyeqManagerLinkProgressResSchema>;

// GET /api/eyeq/cabinet/linkTask/:asset_id/image — binary endpoint (R2 bytes,
// preview=1 prefers the offline JPEG like the manager twin). Assignee-scoped:
// a foreign asset answers exactly like a missing one. Query contract only, no
// res schema (contracts.md).
